import React, { useCallback, useEffect, useRef, useState } from "react";
import { ALLOC_DOCS_URL, ALLOC_DOCS_DELETE, COMM_DOCS_URL, COMM_DOCS_DELETE } from "./constants";
import { authFetch } from "../../../api/api"; 

export default function DocumentsModal({ open, item, type, onClose, onChanged }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const listUrl = type === "commission" ? COMM_DOCS_URL(item?.id) : ALLOC_DOCS_URL(item?.id);
  const deleteUrl = (docId) =>
    type === "commission" ? `${COMM_DOCS_DELETE}${docId}/` : `${ALLOC_DOCS_DELETE}${docId}/`;

  const reload = useCallback(() => {
    if (!item?.id) return Promise.resolve();
    setLoading(true);
    return authFetch(listUrl)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("HTTP " + r.status))))
      .then(setDocs)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [listUrl, item?.id]);

  useEffect(() => {
    if (!open || !item) return;
    setError("");
    setDocs(item.documents || []);
    reload();
  }, [open, item?.id, reload]);

  if (!open || !item) return null;

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    setError("");
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await authFetch(listUrl, { method: "POST", body: fd });
        if (!r.ok) throw new Error(await r.text());
      }
      await reload();
      onChanged?.();
    } catch (err) {
      setError(String(err));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Удалить документ?")) return;
    setError("");
    try {
      const r = await authFetch(deleteUrl(docId), { method: "DELETE" });
      if (!r.ok) throw new Error(await r.text());
      setDocs((prev) => prev.filter((d) => d.id !== docId));
      onChanged?.();
    } catch (err) {
      setError(String(err));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">
            Документы {type === "commission" ? "комиссии" : "платежа фабрике"} #{item.id}
          </h2>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-800">✕</button>
        </div>
        {error && (
          <div className="mb-3 whitespace-pre-wrap rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">{error}</div>
        )}
        {loading ? (
          <p className="text-sm text-slate-500">Загрузка…</p>
        ) : docs.length === 0 ? (
          <p className="text-sm text-slate-500">Документов пока нет.</p>
        ) : (
          <ul className="space-y-2">
            {docs.map((doc) => (
              <li key={doc.id} className="flex items-center justify-between gap-2 rounded border p-2 text-sm">
                <a href={doc.file_url || doc.file} target="_blank" rel="noreferrer" className="truncate text-blue-700 hover:underline">
                  📄 {doc.original_name || `Документ #${doc.id}`}
                </a>
                <button type="button" onClick={() => handleDelete(doc.id)} className="shrink-0 text-xs text-red-600 hover:underline">
                  🗑 Удалить
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4 flex items-center gap-2">
          <input ref={inputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={handleUpload} />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            {uploading ? "Загрузка…" : "⬆ Добавить файлы"}
          </button>
          <button type="button" onClick={onClose} className="ml-auto rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}